import config from "console_config";

import { COMPONENT_SEVERITY, EXCLUDED_INCIDENT_STATUSES } from "../constants";
import {
  Data,
  Incident,
  NewStatusIncident,
  NewStatusIncidentComponent,
  NewStatusIncidentsResponse,
  StatusPageStatus
} from "../types";

const getWorstComponentStatus = (
  components: NewStatusIncidentComponent[]
): string =>
  components.reduce(
    (worst, c) =>
      (COMPONENT_SEVERITY[c.status] ?? 0) > (COMPONENT_SEVERITY[worst] ?? 0)
        ? c.status
        : worst,
    "none"
  );

// When no component carries a degraded status, fall back to the incident's
// lifecycle stage to derive severity (matches status page website logic).
const deriveIncidentFallbackSeverity = (
  incident: NewStatusIncident
): number => {
  if (incident.status === "investigating" || incident.status === "identified") {
    return COMPONENT_SEVERITY.major_outage;
  }
  if (incident.status === "monitoring") {
    return COMPONENT_SEVERITY.degraded_performance;
  }
  return COMPONENT_SEVERITY.operational;
};

const getEffectiveSeverity = (incident: NewStatusIncident): number => {
  const worstComponent = Math.max(
    ...incident.components.map(c => COMPONENT_SEVERITY[c.status] ?? 0)
  );

  if (worstComponent > COMPONENT_SEVERITY.operational) {
    return worstComponent;
  }

  return deriveIncidentFallbackSeverity(incident);
};

export const deriveStatusFromIncidents = (
  incidents: NewStatusIncident[]
): StatusPageStatus => {
  const active = incidents.filter(
    i => !EXCLUDED_INCIDENT_STATUSES.has(i.status)
  );

  if (active.length === 0) {
    return { indicator: "none", description: "All Systems Operational" };
  }

  const worstSeverity = Math.max(...active.map(getEffectiveSeverity));

  if (worstSeverity >= COMPONENT_SEVERITY.major_outage) {
    return { indicator: "critical", description: "Major System Outage" };
  }

  if (worstSeverity >= COMPONENT_SEVERITY.partial_outage) {
    return { indicator: "major", description: "Partial System Outage" };
  }

  if (worstSeverity >= COMPONENT_SEVERITY.degraded_performance) {
    return {
      indicator: "minor",
      description: "Minor Service Degradation"
    };
  }

  return { indicator: "none", description: "All Systems Operational" };
};

const transformNewIncidentToLegacy = (
  incident: NewStatusIncident
): Incident => ({
  id: incident.id,
  name: incident.description,
  status: incident.status,
  impact: getWorstComponentStatus(incident.components),
  shortlink: `${config.URL_STATUS_API}/incidents/${incident.id}`,
  region_ids: incident.region_ids
});

export const fetchFromNewStatusApi = async (): Promise<Data> => {
  const response = await fetch(`${config.URL_STATUS_API}/data/incidents.json`);

  if (!response.ok) {
    throw new Error(`Status API responded with ${response.status}`);
  }

  const data: NewStatusIncidentsResponse = await response.json();

  const incidents: Incident[] = data.incidents.map(
    transformNewIncidentToLegacy
  );

  const status: StatusPageStatus = {
    ...deriveStatusFromIncidents(data.incidents),
    url: config.URL_STATUS_API
  };

  return {
    status,
    incidents,
    scheduled_maintenances: []
  };
};
